import type { ComponentDefinition } from "../types";

// Magnum Energy MSH3012RV Owner's Manual, 64-0089 Rev E (April 2023),
// printed pp1, 14, 19–22, 32–38 and 49. This is the original MSH3012RV,
// not a different MSH-M model or the later MSH3012RV-L variant.
// https://www.sensata.com/sites/default/files/a/sensata-msh-3012rv-inverter-charger-manual.pdf
export const MAGNUM: ComponentDefinition[] = [
  {
    id: "shore-magnum-msh3012rv",
    name: "Magnum Energy MSH3012RV Inverter/Charger",
    shortName: "MSH3012RV",
    subtitle: "Inverter/charger 3000VA, 120V",
    category: "charger",
    manufacturer: "Magnum Energy",
    model: "MSH3012RV",
    voltage: 12,
    acVoltage: 120,
    compatibleVoltages: [12],
    // Exact original-model row publishes 3000 W (manual Appendix A: 3000 VA).
    // https://dmx-power.com/catalog/inverters/msh-rv-series
    watts: 3000,
    // Table 2-1 and Appendix A publish this directly; do not estimate the
    // worst-case DC input from peak efficiency or an AC surge-current rating.
    ampDraw: 400,
    ampOutput: 125,
    width: 270,
    height: 180,
    terminals: [
      { id: "ac-in-l1", label: "HOT 1 IN", polarity: "neutral", direction: "input", side: "left", offset: 0.12, maxAmp: 50 },
      { id: "ac-in-l2", label: "HOT 2 IN", polarity: "neutral", direction: "input", side: "left", offset: 0.26, maxAmp: 50 },
      { id: "ac-in-n", label: "NEUT IN", polarity: "neutral", direction: "input", side: "left", offset: 0.4, maxAmp: 50 },
      // This is one bidirectional physical DC pair. The existing input
      // convention supports charging and inverting without duplicate posts.
      // maxAmp is the published continuous-current envelope, not a lug test.
      { id: "dc-pos", label: "DC +", polarity: "positive", direction: "input", side: "left", offset: 0.66, maxAmp: 400 },
      { id: "dc-neg", label: "DC −", polarity: "negative", direction: "input", side: "left", offset: 0.86, maxAmp: 400 },
      // Table 2-3: HOT1 reaches 60 A with load support; HOT2 passes 50 A.
      // In inverter mode both outputs share 25 A at the same 120 V phase.
      // The neutral must support the single-input 60 A configuration.
      { id: "ac-out-l1", label: "HOT 1 OUT", polarity: "neutral", direction: "output", side: "right", offset: 0.25, maxAmp: 60 },
      { id: "ac-out-l2", label: "HOT 2 OUT", polarity: "neutral", direction: "output", side: "right", offset: 0.5, maxAmp: 50 },
      { id: "ac-out-n", label: "NEUT OUT", polarity: "neutral", direction: "output", side: "right", offset: 0.75, maxAmp: 60 },
      // §2.5.4 / Fig 2-8: two AC equipment-ground terminals; §2.4.5 /
      // Fig 1-2: a separate DC equipment-ground lug on the chassis.
      // Ground-wire sizing is specified separately from load-current ratings.
      // These drawing connections do not add a DC-negative or neutral bond;
      // the inverter's grounding/neutral relay remains outside the model.
      { id: "ac-in-pe", label: "AC IN PE", polarity: "ground", direction: "bidi", side: "top", offset: 0.15, maxAmp: 0 },
      { id: "chassis", label: "CHASSIS", polarity: "ground", direction: "bidi", side: "top", offset: 0.5, maxAmp: 0 },
      { id: "ac-out-pe", label: "AC OUT PE", polarity: "ground", direction: "bidi", side: "top", offset: 0.85, maxAmp: 0 },
      // §1.3 / §3.13: this model uses the green NETWORK RJ11 port for
      // ME-RC / ME-ARC remotes. This is not Ethernet or a DC return path.
      { id: "network", label: "NETWORK RJ11", polarity: "signal", direction: "bidi", side: "bottom", offset: 0.5, maxAmp: 0, color: "#22c55e" },
    ],
  },
];

// Exact -L model: Sensata 64-0503 Rev B (October 2020), and the current
// manufacturer page link to MSH-RV manual 64-0089 Rev F (July 2026).
// https://dmx-power.com/catalog/inverters/msh-rv-series?v=MSH3012RV-L
// https://dmx-power.com/sites/default/files/document/manual/64-0089-W%20F%20Owners%20Manual%2C%20MSH3012RV%20Series.pdf
// Rev F Tables 2-1/2-2/2-3 confirm the same physical DC/AC arrangement.
// Retain the legacy entry's id and a separate exact -L identity for saved plans.
MAGNUM.push({
  ...MAGNUM[0],
  id: "shore-magnum-msh3012rv-l",
  name: "Magnum Energy MSH3012RV-L Inverter/Charger",
  shortName: "MSH3012RV-L",
  model: "MSH3012RV-L",
  image: "shore-magnum-msh3012rv-l.webp",
  terminals: MAGNUM[0].terminals.map(terminal => ({ ...terminal })),
});
